import React, { useContext, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardTransition,
  visuallyHidden,
  zIndexLayers,
} from '../styles';
import { Button, Link } from './ui';
import MegaMenu from './MegaMenu';
import { StoreContext } from '../context/StoreContext';

const StyledNavigation = styled.nav``;

const StyledToggle = styled.button`
  ${maxBreakpointQuery.mlarge`
    position: relative;
    margin: 0;
    padding: 6px 0;
    height: 100%;
    background: none;
    border: none;
  `}

  ${minBreakpointQuery.mlarge`
    display: none;
  `}

  span {
    ${maxBreakpointQuery.mlarge`
      display: block;
      margin-top: 6px;
      margin-bottom: 6px;
      height: 2px;
      width: 23px;
      background-color: ${brandColours.secondary};
      transition: ${standardTransition('transform')};
    `}
  }

  ${({ displayNav }) => {
    if (displayNav) {
      return css`
        ${maxBreakpointQuery.mlarge`
          z-index: ${zIndexLayers.fourth};
        `}

        span {
          &:nth-child(1) {
            ${maxBreakpointQuery.mlarge`
              transform: translateY(8px) rotate(-45deg);
            `}
          }

          &:nth-child(2) {
            ${maxBreakpointQuery.mlarge`
              opacity: 0;
            `}
          }

          &:nth-child(3) {
            ${maxBreakpointQuery.mlarge`
              transform: translateY(-8px) rotate(45deg);
            `}
          }
        }
      `;
    }
  }}
`;

const StyledList = styled.ul`
  ${maxBreakpointQuery.mlarge`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    z-index: ${zIndexLayers.third};
    display: ${({ displayNav }) => (displayNav ? 'flex' : 'none')};
    flex-direction: column;
    gap: 10px;
    padding-top: 100px;
    padding-bottom: 20px;
    height: 100%;
    background-color: ${brandColours.primary};
  `}

  ${minBreakpointQuery.mlarge`
    display: flex;
  `}
`;

// Moved up for hover
const StyledMegaMenu = styled(MegaMenu)`
  ${minBreakpointQuery.mlarge`
    top: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: ${zIndexLayers.third};
    width: 100%;
    left: -9999em;
  `}

  ${({ active }) => {
    if (!active) {
      return css`
        ${maxBreakpointQuery.mlarge`
          ${visuallyHidden()};
        `}
      `;
    }
  }}
`;

const StyledItem = styled.li`
  ${minBreakpointQuery.mlarge`
    padding: 30px 15px;
  `}

  ${minBreakpointQuery.large`
    padding-right: 20px;
    padding-left: 20px;
  `}

  ${minBreakpointQuery.xlarge`
    padding-right: 25px;
    padding-left: 25px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding-right: 30px;
    padding-left: 30px;
  `}
  
  &:hover {
    ${StyledMegaMenu} {
      ${minBreakpointQuery.mlarge`
        left: 0;
      `}
    }
  }
`;

const StyledLink = styled(Link)`
  color: ${brandColours.secondary};
  font-weight: ${fontWeights.semibold};
  transition: ${standardTransition('color')};

  ${maxBreakpointQuery.mlarge`
    display: block;
    padding: 5px 30px;
    ${fontSize(18)};
  `}

  span {
    position: relative;
    display: block;
    padding-right: 32px;

    ${minBreakpointQuery.mlarge`
      padding-right: 20px;
    `}

    &:before,
    &:after {
      ${maxBreakpointQuery.mlarge`
        content: '';
        position: absolute;
        top: 50%;
        right: 0;
        display: block;
        border: 2px solid ${brandColours.secondary};
      `}
    }

    &:before {
      ${maxBreakpointQuery.mlarge`
        height: 20px;
        width: 20px;
        border-radius: 50%;
        transform: translateY(-50%);
      `}
    }

    &:after {
      ${maxBreakpointQuery.mlarge`
        height: 6px;
        width: 6px;
        border-bottom: none;
        border-left: none;
        border-radius: 1px;
        transform: translate(-8px, -50%) rotate(45deg);
      `}

      ${minBreakpointQuery.mlarge`
        content: '';
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        width: 0; 
        height: 0; 
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 4px solid ${brandColours.secondary};
        border-radius: 1px;
        transition: ${standardTransition('border-top-color')};
      `}
    }
  }

  &:hover,
  &.current-page {
    color: ${brandColours.quaternary};

    span {
      &:after {
        ${minBreakpointQuery.mlarge`
          border-top-color: ${brandColours.quaternary};
        `}
      }
    }
  }
`;

const StyledButtonWrapper = styled.div`
  width: 100%;
  max-width: 340px;
  margin: 20px auto 0 auto;
  padding: 0 30px;

  ${minBreakpointQuery.mlarge`
    ${visuallyHidden()}
  `}
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const NavItem = ({ text, link, megaMenu, isServices, isIndustries }) => {
  const [displayList, setDisplayList] = useState(false);
  const { setOverlayActive } = useContext(StoreContext);

  const hasMegaMenu = megaMenu.items.length > 0 || isServices || isIndustries;

  return (
    <>
      {hasMegaMenu ? (
        <StyledItem
          onMouseOver={() => {
            setOverlayActive(true);
          }}
          onMouseOut={() => {
            setOverlayActive(false);
          }}
        >
          <StyledLink
            as={link ? Link : 'span'}
            to={link}
            activeClassName="current-page"
            onClick={e => {
              if (
                matchMedia(`(max-width: ${breakpointSizes.mlarge}px)`).matches
              ) {
                e.preventDefault();
                setDisplayList(!displayList);
              }
            }}
          >
            <span>{text}</span>
          </StyledLink>
          <StyledMegaMenu
            {...megaMenu}
            isServices={isServices}
            isIndustries={isIndustries}
            active={displayList}
          />
        </StyledItem>
      ) : (
        <StyledItem>
          <StyledLink to={link} activeClassName="current-page">
            {text}
          </StyledLink>
        </StyledItem>
      )}
    </>
  );
};

const Navigation = ({ displayNav, setDisplayNav, link }) => {
  const {
    allDatoCmsNavigation: { nodes },
  } = useStaticQuery(graphql`
    query NavigationQuery {
      allDatoCmsNavigation(sort: { position: ASC }) {
        nodes {
          id
          text
          link {
            ...LinkFragment
          }
          megaMenuOverline
          megaMenuText
          megaMenuItems {
            image {
              url
              alt
            }
            link {
              text
              pageUrl {
                ...LinkFragment
              }
            }
          }
          megaMenuCtaText
          megaMenuCtaLink {
            text
            pageUrl {
              ...LinkFragment
            }
          }
          isServices
          isIndustries
        }
      }
    }
  `);

  return (
    <StyledNavigation>
      <StyledToggle
        aria-label="Open / Close Navigation"
        displayNav={displayNav}
        onClick={() => {
          setDisplayNav(!displayNav);
        }}
      >
        <span />
        <span />
        <span />
      </StyledToggle>
      <StyledList displayNav={displayNav}>
        {nodes.map(
          (
            {
              text,
              link,
              megaMenuOverline,
              megaMenuText,
              megaMenuItems,
              megaMenuCtaText,
              megaMenuCtaLink,
              isServices,
              isIndustries,
            },
            i,
          ) => (
            <NavItem
              key={i}
              text={text}
              link={link}
              megaMenu={{
                overline: megaMenuOverline,
                text: megaMenuText,
                items: megaMenuItems,
                cta: { text: megaMenuCtaText, link: megaMenuCtaLink },
              }}
              isServices={isServices}
              isIndustries={isIndustries}
            />
          ),
        )}
        <StyledButtonWrapper>
          <StyledButton to={link.pageUrl}>{link.text}</StyledButton>
        </StyledButtonWrapper>
      </StyledList>
    </StyledNavigation>
  );
};

export default Navigation;
